import React, { useEffect, useMemo, useState } from "react";
import { getSkill } from "../../../lib/inventorySkills";
import { useSeasonStats } from "../../../lib/seasonStats";
import { computeFinishing, useFinishingLibrary } from "../../../lib/finishing";
import { computeBallHandling, bhList, useBallHandling } from "../../../lib/ballHandling";
import { BallHandlingStatsPanel } from "./BallHandlingStatsPanel";
import { BallHandlingOutcomes } from "./BallHandlingOutcomes";
import { PlanLink } from "../PlanLink";
import { ItemRow } from "../ItemRow";

export const EMPTY_BH = { outcome_tags: [], flags: [], checkpoints: [], limiting_factors: [] };

// Ball Handling / Passing = same 4 sections as Finishing: stats panel → rate the 8 interactions (+2 from Tactical) → outcome tags → the plan.
export function BallHandlingCategory({ playerId, items, byId, ballHandling, onBallHandlingChange, finishing, onFinishingChange, rowProps, readOnly }) {
  const lib = useBallHandling();
  const finLib = useFinishingLibrary();
  const { records } = useSeasonStats(playerId);
  const bh = ballHandling || EMPTY_BH;
  const [season, setSeason] = useState(bh.season || null);
  useEffect(() => { if (!season && records.length) setSeason(bh.season && records.some(r => r.season === bh.season) ? bh.season : records[0].season); }, [records, season, bh.season]);
  const rec = records.find(r => r.season === season);
  const fin = useMemo(() => (rec && finLib ? computeFinishing(rec.stats, finLib.cutoffs) : null), [rec, finLib]);
  const result = useMemo(() => computeBallHandling(rec?.stats, fin), [rec, fin]);
  useEffect(() => {
    if (readOnly || !season) return;
    const flags = result?.flags || [];
    if (season !== bh.season || JSON.stringify(flags) !== JSON.stringify(bh.flags || [])) onBallHandlingChange(s => ({ ...(s || EMPTY_BH), season, flags }));
  }, [season, result]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!lib) return <div className="text-[10px] text-[#757575]">Loading Ball Handling / Passing…</div>;
  const order = bhList(lib, "Interaction").map(r => r.id);
  const ordered = [...items].sort((a, b) => (order.indexOf(a.id) + 1 || 99) - (order.indexOf(b.id) + 1 || 99));
  const linked = bhList(lib, "Linked Skill").map(r => getSkill(r.id)).filter(s => s && s.status === "active");
  const look = new Set((result?.flags || []).flatMap(f => bhList(lib, "Stat Flag").find(x => x.id === f)?.points_to || []));
  const row = (s, fromCat) => (
    <div key={s.id} className="relative" data-testid={`bh-skill-${s.id}`}>
      {(look.has(s.id) || fromCat) && <div className="absolute right-3 -top-2 z-10 flex items-center gap-1.5">
        {look.has(s.id) && <span className="px-1.5 py-0.5 bg-[#f59e0b] text-[#121212] text-[8px] uppercase tracking-[0.2em] font-bold" data-testid={`bh-look-${s.id}`}>Look at this</span>}
        {fromCat && <span className="px-1.5 py-0.5 border border-white/20 bg-[#1c1c1c] text-[#a0a0a0] text-[8px] uppercase tracking-[0.2em]" data-testid={`bh-linked-${s.id}`}>From {fromCat}</span>}</div>}
      <ItemRow skill={s} rating={byId[s.id]} readOnly={readOnly} {...rowProps(s)} />
    </div>
  );
  return (
    <div className="space-y-3" data-testid="bh-category">
      <BallHandlingStatsPanel playerId={playerId} lib={lib} records={records} season={season} setSeason={setSeason} result={result} />
      <div className="border border-white/10 bg-[#141414] p-4 space-y-2" data-testid="bh-ratings">
        <div className="flex items-center gap-3 flex-wrap"><span className="text-[9px] font-mono text-[var(--org-accent)]">2</span><div className="text-[10px] uppercase tracking-[0.35em] font-bold text-white">Rate the interactions</div><div className="text-[10px] text-[#757575]">Anchor / Good / Rate Limiter · the stats inform, the coach rates</div></div>
        <div className="space-y-3 pt-1">{ordered.map(s => row(s, null))}{linked.map(s => row(s, "Tactical"))}</div>
      </div>
      <BallHandlingOutcomes lib={lib} bh={bh} byId={byId} readOnly={readOnly} onChange={onBallHandlingChange} finishing={finishing} onFinishingChange={onFinishingChange} />
      <PlanLink playerId={playerId} sectionKey="ball_handling" />
    </div>
  );
}
